import { useEffect, useState } from 'react'
import { BellIcon, BellOffIcon } from './Icons'
import { IconButton } from './IconButton'
import { useT } from '../lib/i18n'
import {
  enablePushNotifications,
  needsIosInstallHint,
  pushCapability,
  syncPushSubscription,
} from '../lib/push'

type Permission = 'default' | 'granted' | 'denied' | 'unavailable'

function currentPermission(): Permission {
  if (pushCapability() !== 'available') return 'unavailable'
  return Notification.permission
}

type NotificationToggleProps = {
  /**
   * `icon` for the header; `row` for the overflow menu, where a labelled line is the honest shape
   * for a setting — an unlabelled bell in a list of unlabelled icons is how the old header ended up
   * with five glyphs and no words.
   */
  variant?: 'icon' | 'row'
}

/**
 * The push subscription must be created from a real user gesture, so it is a button — never an
 * effect. On iOS in a Safari tab the capability gate returns `ios-tab` and we show the install
 * hint instead.
 */
export function NotificationToggle({ variant = 'icon' }: NotificationToggleProps) {
  const t = useT()
  const [permission, setPermission] = useState<Permission>(() => currentPermission())
  const [busy, setBusy] = useState(false)
  const capability = pushCapability()

  useEffect(() => {
    // Re-POST the existing subscription on every open: endpoints rotate and the api prunes dead
    // ones, which is the only pruning mechanism that works uniformly (web-push-pwa.md).
    void syncPushSubscription()
  }, [])

  if (capability === 'unsupported') return null

  if (capability === 'ios-tab' || (needsIosInstallHint() && permission === 'unavailable')) {
    if (variant === 'row') {
      return (
        <p className="flex min-h-12 items-center gap-3 px-3 text-ui text-muted">
          <BellOffIcon size={20} />
          {t('chat.notifications')}
          <span className="ms-auto text-meta">{t('chat.addToHomeScreen')}</span>
        </p>
      )
    }
    return <span className="rounded-full bg-surface2 px-3 py-1 text-meta text-muted">{t('chat.addToHomeScreen')}</span>
  }

  const state = permission === 'granted' ? 'granted' : permission === 'denied' ? 'denied' : 'off'

  if (variant === 'row') {
    return (
      <button
        type="button"
        disabled={busy || permission === 'denied'}
        onClick={() => {
          setBusy(true)
          void enablePushNotifications()
            .then(() => setPermission(currentPermission()))
            .finally(() => setBusy(false))
        }}
        className="flex min-h-12 w-full items-center gap-3 px-3 text-start text-ui text-ink transition-colors duration-quick hover:bg-surface2 disabled:opacity-60"
      >
        {state === 'granted' ? <BellIcon size={20} className="text-accent" /> : <BellOffIcon size={20} />}
        {t('chat.notifications')}
        <span className="ms-auto text-meta text-muted">
          {state === 'granted'
            ? t('chat.notificationStateOn')
            : state === 'denied'
              ? t('chat.notificationStateBlocked')
              : t('chat.notificationStateOff')}
        </span>
      </button>
    )
  }

  if (permission === 'granted') {
    return (
      <IconButton label={t('chat.notificationsEnabled')} variant="plain" disabled>
        <BellIcon size={20} className="text-accent" />
      </IconButton>
    )
  }

  return (
    <IconButton
      label={permission === 'denied' ? t('chat.notificationsBlockedInSettings') : t('chat.enableNotifications')}
      variant="plain"
      disabled={busy || permission === 'denied'}
      onClick={() => {
        setBusy(true)
        void enablePushNotifications()
          .then(() => setPermission(currentPermission()))
          .finally(() => setBusy(false))
      }}
    >
      <BellOffIcon size={20} />
    </IconButton>
  )
}
